import { NativeBiometric, BiometryType } from '@capgo/capacitor-native-biometric';

const SERVER_KEY = 'twenty4seven-app';
const ENABLED_FLAG = 'faceid_enabled';
// Which account the saved biometric login belongs to (one per device).
const USER_KEY = 'faceid_username';

export async function isBiometricAvailable() {
  try {
    const result = await NativeBiometric.isAvailable();
    return result.isAvailable;
  } catch {
    return false;
  }
}

// What to call the biometric method in the UI. iPhones have Face ID or
// Touch ID; most Android phones use fingerprint, some use face unlock.
// Returns e.g. "Face ID", "fingerprint", "biometrics".
export async function getBiometricLabel() {
  try {
    const { biometryType } = await NativeBiometric.isAvailable();
    switch (biometryType) {
      case BiometryType.FACE_ID: return 'Face ID';
      case BiometryType.TOUCH_ID: return 'Touch ID';
      case BiometryType.FINGERPRINT: return 'fingerprint';
      case BiometryType.FACE_AUTHENTICATION: return 'face unlock';
      default: return 'biometrics';
    }
  } catch {
    return 'biometrics';
  }
}

export function isBiometricEnabled() {
  return localStorage.getItem(ENABLED_FLAG) === '1';
}

// Username the saved biometric login belongs to, or null if unknown
// (e.g. enabled before this was tracked).
export function getBiometricUsername() {
  try { return localStorage.getItem(USER_KEY); } catch { return null; }
}

// Call after a successful password login, once the user opts in.
// Overwrites any previously saved account - only one per device.
export async function enableBiometricLogin(username, refreshToken) {
  await NativeBiometric.setCredentials({
    username,
    password: refreshToken, // reusing the "password" field to hold the refresh token
    server: SERVER_KEY,
  });
  localStorage.setItem(ENABLED_FLAG, '1');
  localStorage.setItem(USER_KEY, username);
}

export async function disableBiometricLogin() {
  try {
    await NativeBiometric.deleteCredentials({ server: SERVER_KEY });
  } catch {}
  localStorage.removeItem(ENABLED_FLAG);
  localStorage.removeItem(USER_KEY);
}

// Called when the user taps "Log in with Face ID" / "...with fingerprint".
// Returns the refreshed user object on success, or throws.
export async function loginWithBiometric() {
  await NativeBiometric.verifyIdentity({
    reason: 'Log in to Twenty4Seven',
    title: 'Log in',
    subtitle: 'Twenty4Seven',        // Android prompt only
    negativeButtonText: 'Cancel',    // Android prompt only
    maxAttempts: 3,                  // Android only - default is 1, so a single
                                     // misread finger would fail the whole login
  });

  const { username, password: refreshToken } = await NativeBiometric.getCredentials({
    server: SERVER_KEY,
  });

  const refreshResponse = await fetch('/api/auth/refresh', {
    method: 'POST',
    headers: { Authorization: `Bearer ${refreshToken}` },
    credentials: 'include', // lets the response Set-Cookie land in the WebView
  });

  if (!refreshResponse.ok) {
    await disableBiometricLogin(); // stale/revoked token — stop offering Face ID
    throw new Error('Session expired, please log in again');
  }

  const meResponse = await fetch('/api/auth/me', { credentials: 'include' });
  if (!meResponse.ok) throw new Error('Could not load session');

  return meResponse.json();
}
